import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Map, { Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import StepIndicator from "../components/StepIndicator";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ProfileSelector from "../components/ProfileSelector";
import { MapPinIcon } from "@heroicons/react/24/outline";

const analyzeSteps = [
    { number: 1, label: 'Dónde y quién eres', path: '/analizarpropiedad/:id/paso1' },
    { number: 2, label: 'Imágenes', path: '/analizarpropiedad/:id/imagenes' },
    { number: 3, label: 'Información básica', path: '/analizarpropiedad/:id/datos' },
    { number: 4, label: 'Recibir informe', path: '/analizarpropiedad/:id/informe' }
];

const AnalyzePropertyStep1 = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [numericId, setNumericId] = useState(null);
    const [fuente, setFuente] = useState(null); // 'url_idealista' | 'manual' | 'sync_idealista' | null
    const [coordenadas, setCoordenadas] = useState(null); // { lat, lon } cuando viene de Idealista
    const [addressData, setAddressData] = useState(null);
    const [perfil, setPerfil] = useState(null);
    const [initialAddress, setInitialAddress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resolving, setResolving] = useState(true);
    const [error, setError] = useState(null);

    // Modo B: viene de importación de Idealista (tiene coordenadas pero no dirección exacta)
    const modoIdealista = fuente === 'url_idealista' || fuente === 'sync_idealista';

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(
                    `${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/by-public-id/${id}`,
                    { credentials: 'include' }
                );
                if (!res.ok) throw new Error();
                const data = await res.json();
                setNumericId(data.id);
                setPerfil(data.perfil_solicitante);
                setFuente(data.fuente_datos ?? null);

                if (data.latitud && data.longitud) {
                    setCoordenadas({ lat: Number(data.latitud), lon: Number(data.longitud) });
                }

                if (data.direccion) {
                    const preloaded = {
                        direccion: data.direccion,
                        municipio: data.municipio || null,
                        distrito: data.distrito || null,
                        codigoPostal: data.codigo_postal || null,
                        lat: data.latitud ? Number(data.latitud) : null,
                        lon: data.longitud ? Number(data.longitud) : null
                    };
                    setInitialAddress(preloaded);
                    setAddressData(preloaded);
                }
            } catch {
                setError('No se pudo cargar la propiedad.');
            } finally {
                setResolving(false);
            }
        };
        load();
    }, [id]);

    // Modo A: dirección obligatoria. Modo B: solo perfil obligatorio
    const canProceed = modoIdealista ? !!perfil : (addressData && perfil);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canProceed || !numericId) return;
        setLoading(true);
        setError(null);

        try {
            const body = {
                perfil_solicitante: perfil,
                // En Modo B la dirección es opcional
                ...(addressData ? {
                    direccion: addressData.direccion,
                    municipio: addressData.municipio,
                    distrito: addressData.distrito,
                    latitud: addressData.lat,
                    longitud: addressData.lon,
                    codigo_postal: addressData.codigoPostal,
                } : {}),
            };

            const res = await fetch(
                `${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/${numericId}/direccion`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(body)
                }
            );
            if (!res.ok) throw new Error();
            navigate(`/analizarpropiedad/${id}/imagenes`);
        } catch {
            setError('Error al guardar. Por favor, inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    if (resolving) {
        return <div className="max-w-2xl mx-auto px-4 py-8 text-center text-gray-500">Cargando...</div>;
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                <StepIndicator currentStep={1} steps={analyzeSteps} propertyId={id} />

                <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                    ¿Dónde está la propiedad?
                </h1>
                <p className="text-gray-500 text-sm text-center mb-8">
                    {modoIdealista
                        ? 'Hemos obtenido la ubicación aproximada del anuncio.'
                        : 'Escribe la dirección y cuéntanos quién eres para personalizar el análisis.'
                    }
                </p>

                <form onSubmit={handleSubmit} className="space-y-8">

                    {/* MODO B: mapa con ubicación aproximada de Idealista */}
                    {modoIdealista && coordenadas && (
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <MapPinIcon className="w-4 h-4 text-amber-500" />
                                <h2 className="text-base font-semibold text-gray-800">Ubicación aproximada</h2>
                            </div>
                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-2" style={{ height: 220 }}>
                                <Map
                                    mapboxAccessToken={window.MAPBOX_TOKEN}
                                    initialViewState={{
                                        longitude: coordenadas.lon,
                                        latitude: coordenadas.lat,
                                        zoom: 15
                                    }}
                                    style={{ width: '100%', height: '100%' }}
                                    mapStyle="mapbox://styles/mapbox/streets-v12"
                                    interactive={false}
                                >
                                    <Marker longitude={coordenadas.lon} latitude={coordenadas.lat} color="#3B82F6" />
                                </Map>
                            </div>
                            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                                La ubicación es aproximada. El anuncio de Idealista no incluye la dirección exacta.
                            </p>
                        </div>
                    )}

                    {/* Dirección exacta */}
                    <div>
                        <h2 className="text-base font-semibold text-gray-800 mb-1">
                            {modoIdealista ? 'Dirección exacta' : 'Dirección de la propiedad'}
                            {modoIdealista && (
                                <span className="ml-2 text-xs font-normal text-gray-400">(opcional — necesaria para datos catastrales)</span>
                            )}
                        </h2>
                        <AddressAutocomplete
                            placeholder="Ej. Calle Gran Vía 12, Granada"
                            onAddressSelect={setAddressData}
                            initialQuery={initialAddress?.direccion || ''}
                            initialSelected={initialAddress}
                        />
                    </div>

                    {/* Perfil */}
                    <div>
                        <h2 className="text-base font-semibold text-gray-800 mb-3">¿Cuál es tu perfil?</h2>
                        <ProfileSelector selected={perfil} onProfileSelect={setPerfil} />
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={!canProceed || loading}
                            className="inline-flex items-center justify-center py-3 px-8 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                        >
                            {loading ? 'Guardando...' : 'Siguiente: Subir imágenes →'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AnalyzePropertyStep1;
